import PropTypes from 'prop-types';
import React, { memo, Suspense, useCallback } from 'react';
import { Route, Switch } from 'react-router-dom';

import { lazyLoad } from '@/lib/utils';
import LoadingSpinner from 'components/LoadingSpinner';

import ROUTES from './config';
import { FORBIDDEN_PATH, NOT_FOUND_PATH } from './constants';

const NotFoundPage = lazyLoad(() => import('containers/NotFoundPage'));
const ForbiddenPage = lazyLoad(() => import('containers/ForbiddenPage'));
const PrivateRoute = lazyLoad(() => import('./PrivateRoute'));
const PublicRoute = lazyLoad(() => import('./PublicRoute'));

/**
 * AppRoutes
 *
 * This component contains the main routing configuration for the application.
 * It defines all route paths and their corresponding components.
 */
const AppRoutes = ({ componentProps, isLoading, isAuthenticated }) => {
  const renderRoutes = useCallback(
    (routes) =>
      routes.map((route) => {
        const RouteComponent = route.isPrivate ? PrivateRoute : PublicRoute;

        return (
          <RouteComponent
            key={route.path}
            path={route.path}
            exact={route.exact}
            render={() => (
              <route.render
                {...(route.inheritComponentProps && componentProps)}
              />
            )}
            {...(route.inheritIsAuthenticatedState && { isAuthenticated })}
          />
        );
      }),
    [isAuthenticated, componentProps],
  );

  if (isLoading) return null;

  return (
    <Suspense fallback={<LoadingSpinner />}>
      <Switch>
        <Route path={FORBIDDEN_PATH} component={ForbiddenPage} />
        <Route path={NOT_FOUND_PATH} component={NotFoundPage} />

        {renderRoutes(ROUTES)}
      </Switch>
    </Suspense>
  );
};

AppRoutes.propTypes = {
  componentProps: PropTypes.object,
  isLoading: PropTypes.bool,
  isAuthenticated: PropTypes.bool,
};

export default memo(AppRoutes);
